import React, { useState, useEffect } from 'react';
import { useUser } from '../../../UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faEnvelope, faDesktop, faUser, faCog } from '@fortawesome/free-solid-svg-icons';
import './AccountSettings.css';

function AccountSettings() {
    const { user, updateUser } = useUser();
    const [activeSection, setActiveSection] = useState('account');
    const [userData, setUserData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        role: user?.role || '',
        company: user?.Company || '',
        preferredLanguage: user?.PreferredLanguage || ''
    });
    const [saveMessage, setSaveMessage] = useState('');
    const [notifications, setNotifications] = useState([]);
    const [notificationPreferences, setNotificationPreferences] = useState({});

    useEffect(() => {
        // Define available notification types with their descriptions - using EXACT IDs from the backend
        const availableNotifications = [
            { 
                id: 'user_registration', 
                category: 'App', 
                subCategory: 'User Registration', 
                description: 'Receive notifications when new users register on the platform.',
            },
            { 
                id: 'orders', 
                category: 'Ordering System', 
                subCategory: 'Orders', 
                description: 'Status updates for orders being processed in the system.',
            },
            { 
                id: 'order_received', 
                category: 'Shop', 
                subCategory: 'Order Received', 
                description: 'Notifications when new purchase orders are placed.',
            },
            { 
                id: 'whole_genome', 
                category: 'Validation', 
                subCategory: 'Whole Genome', 
                description: 'Status updates for Whole Genome validation tests.',
            },
            { 
                id: 'oral_health_test', 
                category: 'Validation', 
                subCategory: 'Oral Health Test', 
                description: 'Status updates for Oral Health validation tests.',
            },
            { 
                id: 'gut_health_test', 
                category: 'Validation', 
                subCategory: 'Gut Health Test', 
                description: 'Status updates for Gut Health validation tests.',
            },
            { 
                id: 'transcriptome', 
                category: 'Validation', 
                subCategory: 'Transcriptome', 
                description: 'Status updates for Transcriptome validation tests.',
            },
        ];
        
        const fetchNotifications = async () => {
            if (!user || !user.id) {
                console.error('Cannot fetch notification preferences: User ID not available');
                return;
            }
            
            try {
                // First set the available notification types for the UI
                setNotifications(availableNotifications);
                
                // Fetch user-specific notification preferences
                const response = await fetch(`https://service3.szapfs.org/cliniciannotificationpreferences/${user.id}`);
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch notification preferences: ${response.status}`);
                }
                
                const userPreferences = await response.json();
                console.log('Fetched user notification preferences:', userPreferences);
                
                // Initialize preferences based on API response only
                const initialPrefs = {};
                
                // Use what the backend returns directly
                userPreferences.forEach(pref => {
                    initialPrefs[pref.notificationTypeId] = {
                        portal: pref.portalEnabled,
                        mail: pref.emailEnabled
                    };
                });
                
                setNotificationPreferences(initialPrefs);
                
            } catch (error) {
                console.error('Error fetching notification preferences:', error);
                // Do not set any default values if the fetch fails
                setNotificationPreferences({});
            }
        };
        
        fetchNotifications();
    }, [user]);

    if (!user) {
        return <div className="settings-loading">Loading...</div>;
    }
    
    const handlePreferenceChange = (id, type, value) => {
        setNotificationPreferences(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [type]: value
            }
        }));
    };

    const saveUserInfo = () => {
        // Create updated user object with user info only
        const updated = {
            ...user,
            ...userData
        };
        
        // Log the JSON before sending
        console.log('Saving user info:', JSON.stringify(updated, null, 2));
        
        // Update user in context/backend
        updateUser(updated);
        
        setSaveMessage('Account information saved successfully');
        setTimeout(() => setSaveMessage(''), 3000);
    };
    
    const saveNotificationSettings = async () => {
        try {
            // Create a flat array of notification preferences exactly as expected by the backend
            const preferencesArray = Object.entries(notificationPreferences).map(([notificationTypeId, values]) => ({
                notificationTypeId, 
                userId: user.id,
                portalEnabled: values.portal,
                emailEnabled: values.mail
            }));
            
            // Log the JSON before sending
            console.log('Saving notification preferences:', JSON.stringify(preferencesArray, null, 2));
            
            // Send the preferences to the backend
            const response = await fetch('https://service3.szapfs.org/cliniciannotificationpreferences', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(preferencesArray)
            });
            
            if (!response.ok) {
                throw new Error(`Failed to save preferences: ${response.status}`);
            }
            
            setSaveMessage('Notification preferences saved successfully');
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (error) {
            console.error('Error saving notification preferences:', error);
            setSaveMessage('Error saving preferences. Please try again.');
            setTimeout(() => setSaveMessage(''), 3000);
        }
    };
    
    // Group notifications by category
    const groupedNotifications = notifications.reduce((acc, notification) => {
        if (!acc[notification.category]) {
            acc[notification.category] = [];
        }
        acc[notification.category].push(notification);
        return acc;
    }, {});

    // Render specific section content
    const renderSectionContent = () => {
        switch(activeSection) {
            case 'account':
                return (
                    <div className="settings-section-content">
                        <div className="settings-section-title">Account Information</div>
                        <div className="settings-info-section">
                            <div className="settings-info-row">
                                <div className="settings-field">
                                    <label>First Name</label>
                                    <input 
                                        type="text" 
                                        value={userData.firstName} readOnly
                                        onChange={(e) => setUserData({...userData, firstName: e.target.value})} 
                                    />
                                </div>
                                <div className="settings-field">
                                    <label>Last Name</label>
                                    <input 
                                        type="text" 
                                        value={userData.lastName}  readOnly
                                        onChange={(e) => setUserData({...userData, lastName: e.target.value})} 
                                    />
                                </div>
                            </div>
                            <div className="settings-info-row">
                                <div className="settings-field">
                                    <label>Email</label>
                                    <input type="email" value={userData.email} readOnly />
                                </div>
                                <div className="settings-field">
                                    <label>Role</label>
                                    <input type="text" value={userData.role} readOnly />
                                </div>
             
                                
                            </div>
                            <div className="settings-info-row">

                            <div className="settings-field">
                                    <label>Company</label>
                                    <input type="text" value={userData.company} readOnly />
                                </div>
                                <div className="settings-field">
                                    <label>Preferred Language</label>
                                    <input type="text" value={userData.preferredLanguage} readOnly />
                                </div>
                                
                            </div>
                        </div>
         
                    </div>
                );
            
            case 'notifications':
                return (
                    <div className="settings-section-content">
                        <div className="settings-section-title">Notification Preferences</div>
                        <div className="settings-notification-wrapper">
                            <div className="settings-notification-header">
                                <div className="settings-notification-type">Notification Type</div>
                                <div className="settings-notification-channels">
                                    <div className="settings-channel-option">
                                        <FontAwesomeIcon icon={faDesktop} /> Portal
                                    </div>
                                    <div className="settings-channel-option">
                                        <FontAwesomeIcon icon={faEnvelope} /> Email
                                    </div>
                                </div>
                            </div>
                            
                            {Object.entries(groupedNotifications).map(([category, items]) => (
                                <div key={category} className="settings-notification-category">
                                    <h3 className="settings-category-title">{category}</h3>
                                    <div className="settings-category-items">
                                        {items.map(notification => (
                                            <div key={notification.id} className="settings-notification-item">
                                                <div className="settings-notification-info">
                                                    <div className="settings-notification-name">{notification.subCategory}</div>
                                                    <div className="settings-notification-description">{notification.description}</div>
                                                </div>
                                                <div className="settings-notification-toggles">
                                                    <div className="settings-toggle-option">
                                                        <input 
                                                            type="checkbox"
                                                            id={`portal-${notification.id}`}
                                                            checked={notificationPreferences[notification.id]?.portal}
                                                            onChange={(e) => handlePreferenceChange(notification.id, 'portal', e.target.checked)}
                                                        />
                                                    </div>
                                                    <div className="settings-toggle-option">
                                                        <input 
                                                            type="checkbox"
                                                            id={`mail-${notification.id}`}
                                                            checked={notificationPreferences[notification.id]?.mail}
                                                            onChange={(e) => handlePreferenceChange(notification.id, 'mail', e.target.checked)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="settings-actions">
                            <button className="settings-save-button" onClick={saveNotificationSettings}>Save Notification Preferences</button>
                        </div>
                    </div>
                );
                
            default:
                return <div>Select a section from the sidebar</div>;
        }
    };

    return (
        <div className="settings-container">
            {saveMessage && <div className="settings-save-message">{saveMessage}</div>}
            
            <div className="settings-layout">
                <div className="settings-sidebar">
                    <div 
                        className={`settings-sidebar-item ${activeSection === 'account' ? 'settings-sidebar-active' : ''}`}
                        onClick={() => setActiveSection('account')}
                    >
                        <FontAwesomeIcon icon={faUser} />
                        <span>Account</span>
                    </div>
                    <div 
                        className={`settings-sidebar-item ${activeSection === 'notifications' ? 'settings-sidebar-active' : ''}`}
                        onClick={() => setActiveSection('notifications')}
                    >
                        <FontAwesomeIcon icon={faBell} />
                        <span>Notifications</span>
                    </div>
                </div>
                
                <div className="settings-main-content">
                    {renderSectionContent()}
                </div>
            </div>
        </div>
    );
}

export default AccountSettings;
